import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Send as SubmitIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Public as PublishIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import type { ContentStatus } from '../../services/api-client';
import ContentStatusChip from './ContentStatusChip';

interface WorkflowActionsProps {
  status: ContentStatus;
  onSubmitForReview: () => void;
  onApprove: () => void;
  onReject: (feedback: string) => void;
  onPublish: () => void;
  onSchedule: (date: string) => void;
  disabled?: boolean;
  canEdit?: boolean;
}

/**
 * WorkflowActions — context-aware action buttons for the editorial workflow.
 * Shows different buttons depending on the current status.
 */
export default function WorkflowActions({
  status,
  onSubmitForReview,
  onApprove,
  onReject,
  onPublish,
  onSchedule,
  disabled = false,
  canEdit = true,
}: WorkflowActionsProps) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');

  const handleReject = () => {
    onReject(rejectFeedback);
    setRejectOpen(false);
    setRejectFeedback('');
  };

  const handleSchedule = () => {
    onSchedule(scheduleDate);
    setScheduleOpen(false);
    setScheduleDate('');
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Workflow
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Status:
            </Typography>
            <ContentStatusChip status={status} />
          </Box>

          {canEdit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Draft → Submit for Review */}
              {status === 'draft' && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<SubmitIcon />}
                  onClick={onSubmitForReview}
                  disabled={disabled}
                  fullWidth
                >
                  Submit for Review
                </Button>
              )}

              {/* In Review → Approve or Reject */}
              {status === 'in_review' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={onApprove}
                    disabled={disabled}
                    fullWidth
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => setRejectOpen(true)}
                    disabled={disabled}
                    fullWidth
                  >
                    Reject
                  </Button>
                </>
              )}

              {/* Approved → Publish or Schedule */}
              {status === 'approved' && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PublishIcon />}
                    onClick={onPublish}
                    disabled={disabled}
                    fullWidth
                  >
                    Publish Now
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    onClick={() => setScheduleOpen(true)}
                    disabled={disabled}
                    fullWidth
                  >
                    Schedule
                  </Button>
                </>
              )}

              {/* Published — show info */}
              {status === 'published' && (
                <Typography variant="body2" color="text.secondary">
                  This article is live on the blog.
                </Typography>
              )}

              {/* Scheduled — show info */}
              {status === 'scheduled' && (
                <Typography variant="body2" color="text.secondary">
                  This article is scheduled for publication.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Article</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide feedback for the author on what needs to be changed.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Feedback"
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            placeholder="Explain what needs to be revised..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!rejectFeedback.trim()}
          >
            Reject & Send Feedback
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Publication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose when this article should go live.
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            label="Publish Date"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSchedule}
            disabled={!scheduleDate}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
